<template>
  <div>
    <!-- V1 Chip Style (default) -->
    <div v-if="!useV2Style">
      <span v-if="totalConfirmations !== undefined">
        <v-chip
          label
          :small="small"
          :x-small="xSmall"
          color="green lighten-4"
          :class="['mr-1', 'green--text', 'text--darken-4', marginClass]"
        >
          <v-icon small :x-small="xSmall">mdi-check-circle</v-icon>
          <span class="ml-1 font-weight-bold">{{ totalConfirmations }}</span>
        </v-chip>
      </span>
      <span v-if="totalAccepted !== undefined && totalAccepted > 0">
        <v-chip
          label
          :small="small"
          :x-small="xSmall"
          color="green lighten-4"
          :class="['mr-1', 'green--text', 'text--darken-4', marginClass]"
        >
          <v-icon small :x-small="xSmall">mdi-star-circle</v-icon>
          <span class="ml-1 font-weight-bold">{{ totalAccepted }}</span>
        </v-chip>
      </span>
      <span v-if="totalIfNecessary !== undefined">
        <v-chip
          label
          :small="small"
          :x-small="xSmall"
          color="orange lighten-4"
          :class="['mr-1', 'orange--text', 'text--darken-4', marginClass]"
        >
          <v-icon small :x-small="xSmall">mdi-minus-circle</v-icon>
          <span class="ml-1 font-weight-bold">{{ totalIfNecessary }}</span>
        </v-chip>
      </span>
      <span v-if="totalCancellations !== undefined">
        <v-chip
          label
          :small="small"
          :x-small="xSmall"
          color="red lighten-4"
          :class="['mr-1', 'red--text', 'text--darken-4', marginClass]"
        >
          <v-icon small :x-small="xSmall">mdi-close-circle</v-icon>
          <span class="ml-1 font-weight-bold">{{ totalCancellations }}</span>
        </v-chip>
      </span>
      <span v-if="totalNotInTown !== undefined">
        <v-chip
          label
          :small="small"
          :x-small="xSmall"
          color="blue lighten-4"
          :class="['mr-1', 'blue--text', 'text--darken-4', marginClass]"
        >
          <v-icon small :x-small="xSmall">mdi-airplane</v-icon>
          <span class="ml-1 font-weight-bold">{{ totalNotInTown }}</span>
        </v-chip>
      </span>
      <span v-if="totalAwaitingResponse !== undefined">
        <v-chip
          label
          :small="small"
          :x-small="xSmall"
          color="brown lighten-4"
          :class="['mr-1', 'brown--text', 'text--darken-4', marginClass]"
        >
          <v-icon small :x-small="xSmall">mdi-help-circle</v-icon>
          <span class="ml-1 font-weight-bold">{{ totalAwaitingResponse }}</span>
        </v-chip>
      </span>
      <span v-if="totalResponses !== undefined">
        <v-chip
          label
          :small="small"
          :x-small="xSmall"
          color="blue-grey lighten-4"
          :class="['mr-1', 'blue-grey--text', 'text--darken-4', marginClass]"
        >
          <v-icon small :x-small="xSmall">mdi-sigma</v-icon>
          <span class="ml-1 font-weight-bold">{{ totalResponses }}</span>
        </v-chip>
      </span>
    </div>

    <!-- V2 Card Style -->
    <v-row v-else dense class="match-height">
      <v-col
        v-for="statistic in visibleStatistics"
        :key="statistic.key"
        cols="2"
        sm="2"
        md="2"
        lg="2"
        class="align-self-center"
      >
        <v-card
          outlined
          :color="getCardColor(statistic.color)"
          max-height="80px"
          class="text-center d-flex justify-center align-center"
          :light="!$vuetify.theme.dark"
          :dark="$vuetify.theme.dark"
        >
          <v-card-text class="pa-0 px-0">
            <div>
              <div
                class="font-weight-medium"
                :class="[
                  $vuetify.breakpoint.smAndDown ? 'text-h6 mb-n2' : 'text-h5 ',
                  getTextColor(statistic.color),
                ]"
              >
                {{ statistic.value }}
              </div>
              <div class="pb-1">
                <v-icon
                  :color="getIconColor(statistic.color)"
                  :size="$vuetify.breakpoint.smAndDown ? 16 : 18"
                >
                  {{ statistic.icon }}
                </v-icon>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
export default {
  name: "response-statistics-card",
  props: {
    small: {
      type: Boolean,
      default: false,
    },
    xSmall: {
      type: Boolean,
      default: false,
    },
    marginClass: {
      type: String,
    },
    useV2Style: {
      type: Boolean,
      default: false,
    },
    totalConfirmations: {
      type: Number,
    },
    totalAccepted: {
      type: Number,
    },
    totalIfNecessary: {
      type: Number,
    },
    totalCancellations: {
      type: Number,
    },
    totalNotInTown: {
      type: Number,
    },
    totalAwaitingResponse: {
      type: Number,
    },
    totalResponses: {
      type: Number,
    },
    totalGuests: {
      type: Number,
    },
  },
  computed: {
    visibleStatistics() {
      const statistics = [
        {
          key: "confirmations",
          value: this.totalConfirmations,
          icon: "mdi-check-circle",
          color: "green",
          condition: this.totalConfirmations !== undefined,
        },
        {
          key: "accepted",
          value: this.totalAccepted,
          icon: "mdi-star-circle",
          color: "green",
          condition: this.totalAccepted !== undefined && this.totalAccepted > 0,
        },
        {
          key: "ifNecessary",
          value: this.totalIfNecessary,
          icon: "mdi-minus-circle",
          color: "orange",
          condition: this.totalIfNecessary !== undefined,
        },
        {
          key: "cancellations",
          value: this.totalCancellations,
          icon: "mdi-close-circle",
          color: "red",
          condition: this.totalCancellations !== undefined,
        },
        {
          key: "notInTown",
          value: this.totalNotInTown,
          icon: "mdi-airplane",
          color: "blue",
          condition: this.totalNotInTown !== undefined,
        },
        {
          key: "awaitingResponse",
          value: this.totalAwaitingResponse,
          icon: "mdi-help-circle",
          color: "brown",
          condition: this.totalAwaitingResponse !== undefined,
        },
        {
          key: "responses",
          value: this.totalResponses,
          icon: "mdi-sigma",
          color: "blue-grey",
          condition: this.totalResponses !== undefined,
        },
      ];

      return statistics.filter((stat) => stat.condition);
    },
  },
  methods: {
    getCardColor(color) {
      // Match the chip background colors exactly
      return `${color} lighten-4`;
    },
    getIconColor(color) {
      // Match the chip icon colors exactly
      return `${color} darken-4`;
    },
    getTextColor(color) {
      // Match the chip text colors exactly
      return `${color}--text text--darken-4`;
    },
  },
};
</script>
